import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-hot-toast';
import type { StoreInfo } from '@/types/store';
import type { Product, ShippingLocation, CartItem, Variation } from '@/types';
import { useStore } from '@/store';
import { useStoreInfo } from '@/lib/store';
import { formatPrice, getCache, setCache, getDomain } from '@/lib/utils';
import { generatePaymentMetadata } from '@/lib/payment-metadata';
import { Button } from '@/components/ui/button';
import { calculateCartItemDiscount, getEffectiveVariationPrice, getEffectiveProductPrice, getVariationPricingType, getProductPricingType } from '@/lib/discount';
import { CheckoutNoteDialog } from '@/components/cart/checkout-note-dialog';
import { LocationSelector } from '@/components/cart/location-selector';
import { CustomerDetailsForm } from '@/components/cart/customer-details-form';
import { CountryStateSelector } from '@/components/cart/country-state-selector';
import { PaymentMethodDialog } from '@/components/cart/payment-method-dialog';
import { DiscountSummary } from '@/components/cart/discount-summary';
import { ShippingOptions } from '@/components/cart/shipping-options';
import CartItemComponent from '@/components/cart/cart-item';
import { isShipbubbleEnabled } from '@/lib/shipbubble';

// Error handling utilities
const handleNetworkError = (error: unknown, toastId?: string): string => {
  if (!navigator.onLine) {
    const message = 'Please check your internet connection';
    toast.error(message, { id: toastId });
    return message;
  }
  
  if (error instanceof TypeError && error.message.includes('fetch')) {
    const message = 'Server is unreachable. Please try again later';
    toast.error(message, { id: toastId });
    return message;
  }

  const message = error instanceof Error ? error.message : 'An unexpected error occurred';
  toast.error(message, { id: toastId });
  return message;
};

const handleApiError = (response: Response, data: any, toastId?: string): string | null => {
  if (!response.ok) {
    let message = 'Failed to process request';
    
    // Handle specific status codes
    switch (response.status) {
      case 400:
        message = data.message || 'Invalid request. Please check your inputs';
        break;
      case 401:
        message = 'Session expired. Please login again';
        break;
      case 403:
        message = 'You don\'t have permission to perform this action';
        break;
      case 404:
        message = 'The requested resource was not found';
        break;
      case 409:
        message = data.message || 'Request conflicts with current state';
        break;
      case 422:
        message = data.message || 'Invalid data provided';
        break;
      case 429:
        message = 'Too many requests. Please try again later';
        break;
      case 500:
        message = 'Server error. Please try again later';
        break;
      default:
        message = data.message || `Error: ${response.status}`;
    }

    toast.error(message, { id: toastId });
    return message;
  }
  
  return null;
};
import { Loader2, AlertCircle, X } from 'lucide-react';

declare global {
  interface Window {
    PaystackPop: any;
    MonnifySDK: any;
  }
}

const getDefaultPaymentMethod = (storeInfo: StoreInfo | null): 'bank' | 'paystack' | 'monnify' | 'stripe' => {
  if (storeInfo?.mb_active === 1) return 'bank';
  if (storeInfo?.stripeActive === 1) return 'stripe';
  if (storeInfo?.pays_active === 1) return 'paystack';
  if (storeInfo?.monify_active === 1) return 'monnify';
  return 'bank'; // fallback
};

export function CartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, removeFromCart, updateCartQuantity, customerDetails, setCustomerDetails, clearCart } = useStore();
  const { storeInfo } = useStoreInfo();
  const [selectedCountry, setSelectedCountry] = useState<string>(getCache('shipping_country') || '');
  const [selectedState, setSelectedState] = useState<string>(getCache('shipping_state') || '');
  const [selectedLocation, setSelectedLocation] = useState<ShippingLocation | null>(null);
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'bank' | 'paystack' | 'monnify' | 'stripe'>(
    getDefaultPaymentMethod(storeInfo)
  );
  const [showCheckoutNote, setShowCheckoutNote] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [allLocations, setAllLocations] = useState<ShippingLocation[]>([]);
  const [_filteredLocations, setFilteredLocations] = useState<ShippingLocation[]>([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [orderPaymentReceipt, setOrderPaymentReceipt] = useState<File | null>(null);
  const [shippingPaymentReceipt, setShippingPaymentReceipt] = useState<File | null>(null);
  const [selectedShippingOption, setSelectedShippingOption] = useState<string>('manual');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [soldOutItems, setSoldOutItems] = useState<number[]>([]);
  const [showSoldOutModal, setShowSoldOutModal] = useState(false);
  const [stockLevels, setStockLevels] = useState<Record<number, { total: number; variations: Record<string, number> }>>({});
  const [showStockUpdateModal, setShowStockUpdateModal] = useState(false);
  const [stockUpdates, setStockUpdates] = useState<Array<{ id: number; name: string; oldQty: number; newQty: number; variation?: string }>>([]);
  const [vatType, setVatType] = useState<string>('');
  const [vatValue, setVatValue] = useState<number>(0);
  const [stockCheckComplete, setStockCheckComplete] = useState(false);

  // Handle loading state and cleanup
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isSubmitting) {
      // Set a timeout to reset loading state if something goes wrong
      timeoutId = setTimeout(() => {
        setIsSubmitting(false);
        toast.error('The request took too long. Please try again.');
      }, 30000); // 30 seconds timeout
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isSubmitting]);

  useEffect(() => {
    setPaymentMethod(getDefaultPaymentMethod(storeInfo));
  }, [storeInfo]);

  // Clean up duplicate variations in cart on mount
  useEffect(() => {
    const cleanedCart = cart.map(item => {
      if (item.selectedVariations && item.selectedVariations.length > 0) {
        // Remove duplicate variations by name, keeping the first occurrence
        const seenNames = new Set<string>();
        const uniqueVariations = item.selectedVariations.filter(v => {
          if (seenNames.has(v.variation)) {
            console.warn(`Removing duplicate variation "${v.variation}" from ${item.produt_name}`);
            return false;
          }
          seenNames.add(v.variation);
          return true;
        });
        
        if (uniqueVariations.length !== item.selectedVariations.length) {
          return { ...item, selectedVariations: uniqueVariations };
        }
      }
      return item;
    });
    
    // Update cart if any duplicates were found
    const hasChanges = cleanedCart.some((item, index) => 
      item.selectedVariations?.length !== cart[index].selectedVariations?.length
    );
    
    if (hasChanges) {
      localStorage.setItem('cart', JSON.stringify(cleanedCart));
      // Force a re-render by updating the cart in the store
      window.location.reload();
    }
  }, []); // Run only once on mount

  useEffect(() => {
    const savedDetails = localStorage.getItem('customerDetails');
    if (savedDetails) {
      setCustomerDetails(JSON.parse(savedDetails));
    }

    if (storeInfo?.checkout_note) {
      setShowCheckoutNote(true);
    }
  }, [setCustomerDetails, storeInfo]);

  useEffect(() => {
    const checkCartItems = async () => {
      const domain = getDomain();
      try {
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/products?domain=${domain}`, {
          headers: {
            'X-Store-ID': storeInfo?.id?.toString() || ''
          }
        });
        const data = await response.json();

        if (!data.status) return;

        const newSoldOutItems: number[] = [];
        const newStockLevels: Record<number, { total: number; variations: Record<string, number> }> = {};
        const updates: Array<{ id: number; name: string; oldQty: number; newQty: number; variation?: string }> = [];

        cart.forEach(cartItem => {
          const currentProduct = data.products.find((p: Product) => p.id === cartItem.id);

          // Only check if product exists and is active
          if (!currentProduct) {
            console.log(`Product ${cartItem.id} not found in products list`);
            newSoldOutItems.push(cartItem.id);
            return;
          }
          
          if (currentProduct.status !== 1) {
            console.log(`Product ${cartItem.id} is inactive (status: ${currentProduct.status})`);
            newSoldOutItems.push(cartItem.id);
            return;
          }

          // If cart item has variations, check variation stock
          if (cartItem.selectedVariations && cartItem.selectedVariations.length > 0) {
            const variationStocks: Record<string, number> = {};
            let allVariationsSoldOut = true;

            cartItem.selectedVariations.forEach(variation => {
              const currentVariation = currentProduct.variations?.find(
                (v: Variation) => v.variation === variation.variation
              );
              const availableStock = currentVariation ? parseInt(currentVariation.v_qty || '0') : 0;
              variationStocks[variation.variation] = availableStock;

              if (availableStock > 0) {
                allVariationsSoldOut = false;
              }

              // Only warn if stock is insufficient (not just different)
              if (availableStock > 0 && availableStock < variation.quantity) {
                console.log(`Stock warning for ${cartItem.produt_name} - ${variation.variation}:`, {
                  availableStock,
                  cartQuantity: variation.quantity,
                  currentVariation
                });
                // Check if we've already warned about this item
                const previousStock = stockLevels[cartItem.id]?.variations[variation.variation];
                if (previousStock === undefined || availableStock < previousStock) {
                  updates.push({
                    id: cartItem.id,
                    name: cartItem.produt_name,
                    oldQty: variation.quantity,
                    newQty: availableStock,
                    variation: variation.variation
                  });
                }
              }
            });

            if (allVariationsSoldOut) {
              newSoldOutItems.push(cartItem.id);
            }

            newStockLevels[cartItem.id] = {
              total: currentProduct.variations?.reduce((sum: number, v: Variation) => sum + parseInt(v.v_qty || '0', 10), 0) || 0,
              variations: variationStocks
            };
          } else {
            // For non-variation products, check totalStock
            const availableStock = parseInt(currentProduct.totalStock?.toString() || '0');
            newStockLevels[cartItem.id] = {
              total: availableStock,
              variations: {}
            };

            if (availableStock === 0) {
              newSoldOutItems.push(cartItem.id);
            } else if (availableStock < cartItem.quantity) {
              // Only warn if stock has actually decreased
              const previousStock = stockLevels[cartItem.id]?.total;
              if (previousStock === undefined || availableStock < previousStock) {
                updates.push({
                  id: cartItem.id,
                  name: cartItem.produt_name,
                  oldQty: cartItem.quantity,
                  newQty: availableStock
                });
              }
            }
          }
        });

        if (updates.length > 0) {
          setStockUpdates(updates);
          setShowStockUpdateModal(true);
        }

        setSoldOutItems(newSoldOutItems);
        setStockLevels(newStockLevels);
        setStockCheckComplete(true);
      } catch (error) {
        console.error('Error checking cart items:', error);
        const message = error instanceof Error ? error.message : 'Failed to check cart items';
        toast.error(message);
        if (!navigator.onLine) {
          toast.error('Please check your internet connection');
        }
        // Even on error, mark check as complete to show items
        setStockCheckComplete(true);
      } finally {
        // Don't reset loading state here - let the main submitOrder function handle it
      }
    };

    // Run check immediately on mount
    checkCartItems();
    
    // Then run every 10 seconds
    const interval = setInterval(checkCartItems, 10000);

    return () => clearInterval(interval);
  }, [cart]);
  const [stockPileEnabled, setStockPileEnabled] = useState(false);


    useEffect(() => {
    const fetchLocations = async () => {
      try {
        const domain = getDomain();
        const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/locations?domain=${domain}`, {
          headers: {
            'X-Store-ID': storeInfo?.id?.toString() || ''
          }
        });
        const data = await response.json();

        if (data.status) {
          const defaultLocations = [
            { location: 'Store Pickup', price: 0, country: 'NG', state: '' }
          ];
          setAllLocations([...defaultLocations, ...data.shippingLocations]);
          setVatType(data.vatType);
          setVatValue(data.vatValue);
          setStockPileEnabled(data.stockPile === 1);
        }
      } catch (error) {
        console.error('Failed to fetch locations:', error);
        const message = error instanceof Error ? error.message : 'Failed to load shipping locations';
        toast.error(message);
        
        // Network error handling
        if (!navigator.onLine) {
          toast.error('Please check your internet connection');
        } else if (error instanceof TypeError && error.message.includes('fetch')) {
          toast.error('Server is unreachable. Please try again later');
        }
        
        // Try to use cached locations if available
        const cachedLocations = localStorage.getItem('shippingLocations');
        if (cachedLocations) {
          const parsedLocations = JSON.parse(cachedLocations);
          setAllLocations(parsedLocations);
          toast('Using cached shipping locations', { icon: 'ℹ️' });
        }
      } finally {
        // Don't reset loading state here - let the main submitOrder function handle it
      }
    };

    fetchLocations();
  }, []);
  

  useEffect(() => {
    setFilteredLocations(filterLocations());
  }, [selectedCountry, selectedState, allLocations]);

  useEffect(() => {
    const cachedCountry = getCache('shipping_country');
    const cachedState = getCache('shipping_state');
    const cachedLocation = getCache('shipping_location');

    if (cachedCountry) {
      setSelectedCountry(cachedCountry);

      if (cachedState) {
        setSelectedState(cachedState);

        if (cachedLocation) {
          const location = allLocations.find(loc =>
            loc.location === cachedLocation &&
            loc.country === cachedCountry &&
            loc.state === cachedState
          );
          if (location) {
            setSelectedLocation(location);
          }
        }
      }
    }
  }, [allLocations]);

  useEffect(() => {
    if (location.state?.orderId && location.state?.paymentStatus === 'success') {
      setShowPaymentDialog(true);
      setPaymentMethod('stripe');
    }
  }, [location.state]);

  const handleStockUpdateConfirm = () => {
    stockUpdates.forEach(update => {
      if (update.variation) {
        handleVariationQuantityChange(update.id, update.variation, update.newQty - update.oldQty);
      } else {
        updateCartQuantity(update.id, update.newQty);
      }
    });
    setShowStockUpdateModal(false);
    setStockUpdates([]);
  };

  const filterLocations = () => {
    if (!allLocations) return [];
    if (selectedCountry && selectedState) {
      return allLocations.filter(location =>
        location.country === selectedCountry &&
        location.state === selectedState
      );
    } else if (selectedCountry) {
      return allLocations.filter(location =>
        location.country === selectedCountry
      );
    }
    return allLocations;
  };

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedState('');
    setSelectedLocation(null);
    setCache('shipping_country', country, 30);
    setCache('shipping_state', '', 30);
    setCache('shipping_location', '', 30);
    setShowCountryDropdown(false);
    setCountrySearch('');
    setStateSearch('');
    setLocationSearch('');
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedLocation(null);
    setCache('shipping_state', state, 30);
    setCache('shipping_location', '', 30);
    setShowStateDropdown(false);
    setStateSearch('');
    setLocationSearch('');
  };

  const handleLocationSelect = (location: ShippingLocation) => {
    setSelectedLocation(location);
    setCache('shipping_location', location.location, 30);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, type: 'order' | 'shipping') => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      if (type === 'order') {
        setOrderPaymentReceipt(file);
        toast.success('Order payment receipt selected');
      } else {
        setShippingPaymentReceipt(file);
        toast.success('Shipping payment receipt selected');
      }
    }
  };

  const handlePaystackPayment = async () => {
    setIsSubmitting(true);

    if (typeof window.PaystackPop === 'undefined') {
      toast.error('Payment gateway is not available. Please try again later.');
      setIsSubmitting(false);
      return;
    }

    if (!storeInfo?.pays_pk) {
      toast.error('Paystack is not properly configured for this store. Please try a different payment method.');
      setIsSubmitting(false);
      return;
    }

    // generate beautiful CTZ order id to use as both reference and our order_id
    const generateCTZ = (domain = '') => {
      const rand = Math.floor(Math.random() * 900000) + 100000;
      let clean = domain.replace(/^https?:\/\//i, '')
        .replace(/^www\./i, '')
        .split('/')[0].split('.')[0];
      let abbr = clean.substring(0, 4).toUpperCase().padEnd(4, 'X');
      return `CTZ_${rand}${abbr}`;
    };
    const orderId = generateCTZ(storeInfo?.domain || '');
    const reference = orderId; // keep same id for paystack reference

    // Build order_items for webhook in backend format with correct pricing
    const paystackOrderItems = cart.map(item => {
      if (item.selectedVariations && item.selectedVariations.length > 0) {
        return item.selectedVariations.map(variation => {
          const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
          const lineTotal = effectiveUnitPrice * variation.quantity;
          return {
            product_id: item.product_id,
            product_name: item.produt_name || 'Product',
            product_variant: variation.variation || 'Nil',
            qty: variation.quantity,
            variant_id: variation.v_id || 0,
            unit_price: effectiveUnitPrice,
            amount: lineTotal,
          };
        });
      }
      const effectiveUnitPrice = getEffectiveProductPrice(item, item.quantity);
      const lineTotal = effectiveUnitPrice * item.quantity;
      return [{
        product_id: item.product_id,
        product_name: item.produt_name || 'Product',
        product_variant: 'Nil',
        qty: item.quantity,
        variant_id: 0,
        unit_price: effectiveUnitPrice,
        amount: lineTotal,
      }];
    }).flat();

    try {
      const cartMetaItems = cart.map(item => {
        if (item.selectedVariations && item.selectedVariations.length > 0) {
          return item.selectedVariations.map(v => ({
            id: item.id,
            quantity: v.quantity,
            selectedVariations: [{ variation: v.variation, quantity: v.quantity }]
          }));
        }
        return { id: item.id, quantity: item.quantity };
      }).flat();

      const baseMetadata = generatePaymentMetadata({
        storeId: storeInfo?.id?.toString() || '',
        paymentType: 'paystack',
        cartItems: cartMetaItems,
        amounts: {
          orderTotal: subtotal,
          shippingTotal: shippingCost,
          vatAmount: vatAmount,
          total
        },
        customerInfo: {
          email: customerDetails?.email || '',
          name: customerDetails?.fullName || ''
        }
      });

      // Webhook expects order_items (stringified) with product_name, product_variant, qty, variant_id, amount + customer_name, customer_phone
      const metadata = {
        ...baseMetadata,
        order_id: orderId,
        order_items: JSON.stringify(paystackOrderItems),
        customer_name: customerDetails?.fullName || '',
        customer_phone: customerDetails?.phone || '',
      };

      // Sanitize and size-check metadata to avoid hitting gateway limits.
      // Trim long strings and fall back to minimal item data if too large.
      const sanitizeItem = (it: any) => ({
        product_id: it.product_id,
        product_name: String(it.product_name || '').slice(0, 100),
        product_variant: String(it.product_variant || 'Nil').slice(0, 100),
        qty: Number(it.qty || it.quantity || 1),
        variant_id: it.variant_id || it.v_id || 0,
        amount: Number(it.amount || it.price || 0)
      });

      let sanitizedItems = paystackOrderItems.map(sanitizeItem);

      // Try full sanitized items first
      let metaCopy: any = { ...metadata, order_items: JSON.stringify(sanitizedItems) };
      const MAX_META_BYTES = 8000; // conservative limit
      try {
        if (JSON.stringify(metaCopy).length > MAX_META_BYTES) {
          // Fall back to minimal representation
          const minimal = sanitizedItems.map((i: any) => ({ product_id: i.product_id, qty: i.qty, variant_id: i.variant_id }));
          metaCopy.order_items = JSON.stringify(minimal);
        }

        // If still too large, remove order_items entirely and keep a short note
        if (JSON.stringify(metaCopy).length > MAX_META_BYTES) {
          delete metaCopy.order_items;
          metaCopy._note = 'order_items omitted due to size';
        }
      } catch (err) {
        console.warn('Metadata sanitization error:', err);
      }

      // Create an `ongoing_order` record on the backend that contains full order
      // details. The Paystack `ref` will be used to match the payment to this
      // ongoing order when the webhook arrives. This avoids creating a final
      // order before payment while still preserving shipping/vat and customer data.
      try {
        const ongoingPayload = {
          store_id: storeInfo?.id,
          order_id: reference,
          // send the sanitized items (includes variant_id) to ensure webhook has authoritative data
          items: sanitizedItems,
          customer_name: customerDetails?.fullName || '',
          customer_email: customerDetails?.email || '',
          customer_phone: customerDetails?.phone || '',
          address: customerDetails?.address || '',
          shipping_cost: shippingCost,
          vat_amount: vatAmount,
          total_amount: total,
          expires_in_minutes: 27
        };

        // Create ongoing order and wait for confirmation before opening Paystack.
        const ongoingRes = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/ongoing-orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Store-ID': storeInfo?.id?.toString() || ''
          },
          body: JSON.stringify(ongoingPayload)
        });

        if (!ongoingRes.ok) {
          const text = await ongoingRes.text().catch(() => '');
          console.warn('Failed to create ongoing order (non-200):', ongoingRes.status, text);
          toast.error('Failed to reserve order before payment. Please try again.');
          setIsSubmitting(false);
          return;
        }

        const ongoingJson = await ongoingRes.json().catch(() => null);
        if (!ongoingJson || !ongoingJson.status) {
          console.warn('Ongoing order creation failed:', ongoingJson);
          toast.error('Failed to reserve order before payment. Please try again.');
          setIsSubmitting(false);
          return;
        }

        // Now that the ongoing order exists, open Paystack checkout
        const handler = window.PaystackPop.setup({
          key: storeInfo.pays_pk,
          email: customerDetails?.email,
          amount: total * 100,
          currency: 'NGN',
          ref: reference,
          metadata: metaCopy,
          callback: async (response: any) => {
            if (response.status === 'success') {
              toast.success('Payment successful! Creating your order...');
              await handlePaystackOrderSubmission(response.reference);
            } else {
              toast.error(`Payment failed: ${response.message || 'Unknown error'}`);
              const failedPayments = JSON.parse(localStorage.getItem('failedPayments') || '[]');
              failedPayments.push({
                gateway: 'paystack',
                reference: reference,
                amount: total,
                timestamp: new Date().toISOString(),
                error: response.message
              });
              localStorage.setItem('failedPayments', JSON.stringify(failedPayments));
            }
            setIsSubmitting(false);
          },
          onClose: async () => {
            toast.error('Payment cancelled by user');
            setIsSubmitting(false);
            // Cleanup the ongoing order since the user cancelled the payment modal
            try {
              await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/ongoing-orders/${reference}?store_id=${storeInfo?.id}`, {
                method: 'DELETE',
                headers: {
                  'X-Store-ID': storeInfo?.id?.toString() || ''
                }
              });
            } catch (err) {
              console.warn('Failed to cleanup ongoing order on cancel:', err);
            }
          }
        });

        handler.openIframe();
      } catch (err) {
        console.warn('Ongoing order creation error:', err);
        toast.error('Failed to reserve order before payment. Please try again.');
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error('Paystack error:', error);
      toast.error('Failed to initialize payment. Please try again.');
      setIsSubmitting(false);
      
      // Log the error for debugging
      const errorLog = JSON.parse(localStorage.getItem('paymentErrors') || '[]');
      errorLog.push({
        gateway: 'paystack',
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error)
      });
      localStorage.setItem('paymentErrors', JSON.stringify(errorLog));
    }
  };

  const handleMonnifyPayment = () => {
    setIsSubmitting(true);

    if (typeof window.MonnifySDK === 'undefined') {
      toast.error('Payment gateway is not available. Please try again later.');
      setIsSubmitting(false);
      return;
    }

    if (!storeInfo?.monify_api || !storeInfo?.monify_contract) {
      toast.error('Monnify is not properly configured for this store. Please try a different payment method.');
      setIsSubmitting(false);
      return;
    }

    try {
      const paymentRef = `ord_${Math.floor(Math.random() * 1000000000)}`;
      
      window.MonnifySDK.initialize({
        amount: total,
        currency: "NGN",
        reference: paymentRef,
        customerName: customerDetails?.fullName,
        customerEmail: customerDetails?.email,
        customerPhoneNumber: customerDetails?.phone,
        apiKey: storeInfo.monify_api,
        contractCode: storeInfo.monify_contract,
        paymentDescription: "Order Payment",
        isTestMode: process.env.NODE_ENV !== 'production',
        metadata: {
          store_id: storeInfo?.id,
          customer_name: customerDetails?.fullName,
          customer_phone: customerDetails?.phone,
        },
        paymentMethods: ["CARD", "ACCOUNT_TRANSFER"],
        onComplete: async function(response: any) {
          if (response.status === "SUCCESS") {
            // Store initial transaction record
            storeTransaction({
              reference: response.transactionReference,
              gateway: 'monnify',
              amount: total,
              status: 'pending'
            });

            toast.success('Payment successful! Creating your order...');
            try {
              const orderResult = await handleMonnifyOrderSubmission(response.transactionReference);
              if (orderResult?.data?.orderId) {
                updateTransactionStatus(response.transactionReference, 'success', orderResult.data.orderId);
              } else {
                updateTransactionStatus(response.transactionReference, 'failed', undefined, 'Order creation failed');
                toast.error('Payment successful but order creation failed. Our team will contact you shortly.');
              }
            } catch (error) {
              console.error('Order creation error:', error);
              updateTransactionStatus(response.transactionReference, 'failed', undefined, error instanceof Error ? error.message : 'Order creation failed');
              toast.error('Payment successful but order creation failed. Our team will contact you shortly.');
            }
          } else {
            toast.error(`Payment failed: ${response.message || 'Unknown error'}`);
            // Store failed transaction
            storeTransaction({
              reference: paymentRef,
              gateway: 'monnify',
              amount: total,
              status: 'failed',
              error: response.message || 'Unknown error'
            });
          }
          setIsSubmitting(false);
        },
        onClose: function() {
          toast.error('Payment cancelled by user');
          storeTransaction({
            reference: paymentRef,
            gateway: 'monnify',
            amount: total,
            status: 'failed',
            error: 'Payment cancelled by user'
          });
          setIsSubmitting(false);
        }
      });
    } catch (error) {
      console.error('Monnify error:', error);
      toast.error('Failed to initialize payment. Please try again.');
      setIsSubmitting(false);
      
      // Log the error for debugging
      const errorLog = JSON.parse(localStorage.getItem('paymentErrors') || '[]');
      errorLog.push({
        gateway: 'monnify',
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : String(error)
      });
      localStorage.setItem('paymentErrors', JSON.stringify(errorLog));
    }
  };

  const calculateVat = (amount: number): number => {
    if (!vatValue || vatValue === 0) return 0;
    
    if (vatType === 'percentage') {
      return (amount * vatValue) / 100;
    } else if (vatType === 'fixed') {
      return vatValue;
    }
    
    return 0;
  };

  // Calculate subtotal with discounts
  const cartTotals = cart.reduce((totals, item) => {
    const { totalOriginalPrice, totalDiscountValue, totalFinalPrice } = calculateCartItemDiscount(item);
    return {
      originalSubtotal: totals.originalSubtotal + totalOriginalPrice,
      totalDiscount: totals.totalDiscount + totalDiscountValue,
      finalSubtotal: totals.finalSubtotal + totalFinalPrice
    };
  }, { originalSubtotal: 0, totalDiscount: 0, finalSubtotal: 0 });

  const subtotal = cartTotals.finalSubtotal;
  const totalDiscount = cartTotals.totalDiscount;
  const originalSubtotal = cartTotals.originalSubtotal;

  const shippingCost = selectedLocation?.price || 0;
  const vatAmount = calculateVat(subtotal + shippingCost);
  const total = subtotal + shippingCost + vatAmount;

  const handleQuantityChange = (id: number, newQuantity: number) => {
    if (newQuantity < 1) return;
    updateCartQuantity(id, newQuantity);
  };

  const handleVariationQuantityChange = (itemId: number, variation: string, change: number) => {
    const item = cart.find(i => i.id === itemId);
    if (!item || !item.selectedVariations) return;

    const updatedVariations = item.selectedVariations.map(v => {
      if (v.variation === variation) {
        const varStock = item.variations.find(pv => pv.variation === variation)?.v_qty || '0';
        const newQuantity = Math.max(0, Math.min(parseInt(varStock), v.quantity + change));
        return { ...v, quantity: newQuantity };
      }
      return v;
    }).filter(v => v.quantity > 0);

    if (updatedVariations.length === 0) {
      removeFromCart(itemId);
    } else {
      updateCartQuantity(itemId, 1, updatedVariations);
    }
  };

  const handleRemoveVariation = (itemId: number, variation: string) => {
    const item = cart.find(i => i.id === itemId);
    if (!item || !item.selectedVariations) return;

    const updatedVariations = item.selectedVariations
      .filter(v => v.variation !== variation);

    if (updatedVariations.length === 0) {
      removeFromCart(itemId);
    } else {
      updateCartQuantity(itemId, 1, updatedVariations);
    }
  };

  const handleCustomerDetailsChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCustomerDetails({ ...customerDetails, [name]: value });
  };

  const getValidationErrors = () => {
    const errors: string[] = [];

    // Location validation
    if (!selectedCountry) errors.push('Please select a country');
    if (!selectedState) errors.push('Please select a state');
    if (!selectedLocation) errors.push('Please select a delivery location');

    // Customer details validation
    if (!customerDetails?.fullName) {
      errors.push('Full name is required');
    } else {
      const [firstName = '', lastName = ''] = customerDetails.fullName.split(' ');
      if (!firstName.trim() || !lastName.trim()) {
        errors.push('Please provide both first and last name');
      }
    }

    // Email validation
    if (!customerDetails?.email) {
      errors.push('Email is required');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerDetails.email)) {
        errors.push('Please enter a valid email address');
      }
    }

    // Phone validation
    if (!customerDetails?.phone) {
      errors.push('Phone number is required');
    } else if (!/^\+?[\d\s-]{10,}$/.test(customerDetails.phone.trim())) {
      errors.push('Please enter a valid phone number');
    }

    // Address validation for delivery (now optional)
    // Address is optional, no validation needed

    return errors;
  };

  const isFormValid = () => {
    const errors = getValidationErrors();
    return errors.length === 0;
  };

  const handleSubmitOrder = async (paymentReference?: string) => {
    // Check store ID first
    if (!storeInfo?.id) {
      toast.error('Store information is missing. Please refresh the page and try again.');
      return;
    }

    if (!selectedLocation) {
      toast.error('Please select a delivery area');
      return;
    }

    if (!customerDetails?.fullName || !customerDetails?.email || !customerDetails?.phone) {
      toast.error('Please fill in all required customer details');
      return;
    }

    // Address is now optional, no validation needed

    // Bank transfer specific validations
    if (paymentMethod === 'bank') {
      if (!storeInfo?.mb_active) {
        toast.error('Bank transfer payment is not available for this store');
        return;
      }

      if (!orderPaymentReceipt) {
        toast.error('Please upload payment receipt');
        return;
      }

      // Validate file type and size for order payment receipt
      if (orderPaymentReceipt.size > 2 * 1024 * 1024) {
        toast.error('Order payment receipt must be less than 2MB');
        return;
      }

      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
      if (!validTypes.includes(orderPaymentReceipt.type)) {
        toast.error('Payment receipt must be an image (JPG, PNG, GIF) or PDF file');
        return;
      }

      // Shipping payment validation
      if (storeInfo?.mb_active === 1 && storeInfo?.sepShipping === 1 && shippingCost > 0) {
        if (!shippingPaymentReceipt) {
          toast.error('Please upload shipping payment receipt');
          return;
        }

        if (shippingPaymentReceipt.size > 2 * 1024 * 1024) {
          toast.error('Shipping payment receipt must be less than 2MB');
          return;
        }

        if (!validTypes.includes(shippingPaymentReceipt.type)) {
          toast.error('Shipping receipt must be an image (JPG, PNG, GIF) or PDF file');
          return;
        }
      }
    }

    const errors = getValidationErrors();
    
    // Add payment method specific validations
    if (paymentMethod === 'paystack' && !storeInfo?.pays_active) {
      errors.push('Paystack payment is not enabled for this store');
    } else if (paymentMethod === 'monnify' && !storeInfo?.monify_active) {
      errors.push('Monnify payment is not enabled for this store');
    } else if (paymentMethod === 'bank' && !storeInfo?.mb_active) {
      errors.push('Bank transfer is not enabled for this store');
    }

    // Add cart validation
    if (cart.length === 0) {
      errors.push('Your cart is empty');
    }

    // Display all validation errors
    if (errors.length > 0) {
      errors.forEach(error => toast.error(error));
      return;
    }

    setIsSubmitting(true);

    try {
      localStorage.setItem('customerDetails', JSON.stringify(customerDetails));

      // For bank transfers, create a transaction reference
      const bankTransferRef = paymentMethod === 'bank' 
        ? `bank_${Math.floor(Math.random() * 1000000000)}`
        : undefined;

      if (bankTransferRef) {
        storeTransaction({
          reference: bankTransferRef,
          gateway: 'bank',
          amount: total,
          status: 'pending'
        });
      }

      const formData = new FormData();
      const domain = getDomain();
      formData.append('domain', domain);
      formData.append('storeId', storeInfo?.id?.toString() || '');
      if (bankTransferRef) {
        formData.append('bankTransferRef', bankTransferRef);
      }

      const orderItems = cart.map(item => {
        if (item.selectedVariations && item.selectedVariations.length > 0) {
          return item.selectedVariations.map(variation => {
            const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
            const lineTotal = effectiveUnitPrice * variation.quantity;
            return {
              product_id: item.id,
              product_name: item.produt_name,
              product_variant: variation.variation,
              qty: variation.quantity,
              variant_id: variation.v_id,
              unit_price: effectiveUnitPrice,
              amount: lineTotal.toString()
            };
          });
        }
        const effectiveUnitPrice = getEffectiveProductPrice(item, item.quantity);
        const lineTotal = effectiveUnitPrice * item.quantity;
        return [{
          product_id: item.id,
          product_name: item.produt_name,
          product_variant: 'Nil',
          qty: item.quantity,
          variant_id: null,
          unit_price: effectiveUnitPrice,
          amount: lineTotal.toString()
        }];
      }).flat();

      formData.append('items', JSON.stringify(orderItems));
      formData.append('country', selectedCountry);
      formData.append('location', selectedLocation.location);
      formData.append('fullName', customerDetails.fullName);
      formData.append('email', customerDetails.email);
      formData.append('phone', customerDetails.phone);
      formData.append('address', customerDetails.address || '');
      formData.append('note', customerDetails.note || '');
      formData.append('paymentMethod', paymentMethod);
      formData.append('orderAmount', subtotal.toString());
      formData.append('shippingAmount', shippingCost.toString());
      formData.append('vatAmount', vatAmount.toString());

      if (paymentReference) {
        formData.append('paymentReference', paymentReference);
      }

      if (location.state?.orderId) {
        formData.append('order_id', location.state.orderId);
      }

      if (orderPaymentReceipt) {
        const reader = new FileReader();
        reader.readAsDataURL(orderPaymentReceipt);
        reader.onload = async () => {
          const base64data = reader.result as string;
          formData.append('paymentReceipt', base64data);

          if (shippingPaymentReceipt) {
            const shippingReader = new FileReader();
            shippingReader.readAsDataURL(shippingPaymentReceipt);
            shippingReader.onload = async () => {
              const shippingBase64data = shippingReader.result as string;
              formData.append('shippingPaymentReceipt', shippingBase64data);
              await submitOrder(formData);
            };
          } else {
            await submitOrder(formData);
          }
        };
      } else {
        await submitOrder(formData);
      }
    } catch (error) {
      console.error('Error preparing order:', error);
      const errorToastId = 'submit-order-error';
      handleNetworkError(error, errorToastId);
      setIsSubmitting(false);
      
      // If this was called after a successful payment, log the error and notify support
      if (paymentReference) {
        const supportMessage = 'Payment was successful but order creation failed. Please contact support with reference: ' + paymentReference;
        toast.error(supportMessage, { id: `${errorToastId}-payment` });
        
        // Log payment error for tracking
        const errorLog = JSON.parse(localStorage.getItem('paymentErrors') || '[]');
        errorLog.push({
          timestamp: new Date().toISOString(),
          reference: paymentReference,
          error: error instanceof Error ? error.message : String(error)
        });
        localStorage.setItem('paymentErrors', JSON.stringify(errorLog));
      }
    }
  };

  const handlePaystackOrderSubmission = async (paymentReference: string) => {
    if (!selectedLocation) {
      toast.error('Please select a delivery area');
      return;
    }

    if (!customerDetails?.fullName || !customerDetails?.email || !customerDetails?.phone) {
      toast.error('Please fill in all required customer details');
      return;
    }

    if (selectedLocation.location !== 'Stock-Pile' && selectedLocation.location !== 'Store Pickup' && !customerDetails?.address) {
      toast.error('Please provide a delivery address');
      return;
    }

    try {
      // Bank-order shape so backend can record product names and amounts with correct pricing
      const orderItems = cart.map(item => {
        if (item.selectedVariations && item.selectedVariations.length > 0) {
          return item.selectedVariations.map(variation => {
            const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
            const lineTotal = effectiveUnitPrice * variation.quantity;
            const pricingType = variation.pricing_type || getVariationPricingType(variation);

            return {
              product_id: item.product_id,
              product_name: item.produt_name || 'Product',
              product_variant: variation.variation || 'Nil',
              qty: variation.quantity,
              variant_id: variation.v_id || null,
              unit_price: effectiveUnitPrice,
              amount: lineTotal,
              pricing_type: pricingType
            };
          });
        }
        const effectiveUnitPrice = getEffectiveProductPrice(item, item.quantity);
        const lineTotal = effectiveUnitPrice * item.quantity;
        const pricingType = getProductPricingType(item);

        return [{
          product_id: item.product_id,
          product_name: item.produt_name || 'Product',
          product_variant: 'Nil',
          qty: item.quantity,
          variant_id: null,
          unit_price: effectiveUnitPrice,
          amount: lineTotal,
          pricing_type: pricingType
        }];
      }).flat();

      // Use Paystack reference as order_id so backend matches webhook-created payment and avoids duplicates
      const orderData = {
        domain: window.location.hostname,
        order_id: paymentReference,
        store_id: storeInfo?.id,
        items: orderItems,
        customer_name: customerDetails?.fullName || '',
        customer_email: customerDetails?.email || '',
        customer_phone: customerDetails?.phone || '',
        address: customerDetails?.address || '',
        country: selectedCountry || '',
        location: selectedLocation?.location || '',
        shipping_address: {
          address: customerDetails?.address || '',
          city: selectedLocation?.location || '',
          state: selectedState || '',
          country: selectedCountry || ''
        },
        billing_address: {
          address: customerDetails?.address || '',
          city: selectedLocation?.location || '',
          state: selectedState || '',
          country: selectedCountry || ''
        },
        notes: customerDetails?.note || '',
        shipping_method: 'standard',
        payment_method: 'paystack',
        payment_reference: paymentReference,
        payment_status: 'completed',
        subtotal: subtotal,
        shippingAmount: shippingCost,
        shipping_cost: shippingCost,
        vat_amount: vatAmount,
        total_amount: total,
        currency: 'NGN'
      };

      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Store-ID': storeInfo?.id?.toString() || ''
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();

      // Backend returns 'status' field and data structure: { status: true, orderID: "...", trackingId: "...", data: { orderId: ... } }
      if (data.status && (data.orderID || data.data?.orderId)) {
        clearCart();
        navigate('/order-complete', {
          state: {
            orderId: data.data?.orderId || data.orderID,
            trackingId: data.trackingId || data.data?.orderNumber
          }
        });
        return data;
      } else {
        throw new Error(data.error || data.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error submitting Paystack order:', error);
      let errorMessage = 'Failed to place order. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast.error(errorMessage);
      toast.error('Payment was successful but order creation failed. Please contact support with reference: ' + paymentReference);
      
      return null;
    }
  };

  const handleMonnifyOrderSubmission = async (paymentReference: string) => {
    const toastId = 'monnify-order';
    
    // Validate requirements
    const errors = getValidationErrors();
    if (errors.length > 0) {
      toast.error(errors[0], { id: toastId });
      return;
    }

    try {
      // Create order items with proper variation IDs and correct pricing
      const orderItems = cart.map(item => {
        if (item.selectedVariations && item.selectedVariations.length > 0) {
          return item.selectedVariations.map(variation => {
            const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
            const lineTotal = effectiveUnitPrice * variation.quantity;
            const pricingType = variation.pricing_type || getVariationPricingType(variation);

            return {
              product_id: item.id,
              product_name: item.produt_name || item.name || 'Product',
              product_variant: variation.variation || 'Nil',
              qty: variation.quantity,
              variant_id: variation.v_id || null,
              unit_price: effectiveUnitPrice,
              amount: lineTotal,
              pricing_type: pricingType
            };
          });
        }
        const effectiveUnitPrice = getEffectiveProductPrice(item, item.quantity);
        const lineTotal = effectiveUnitPrice * item.quantity;
        const pricingType = getProductPricingType(item);

        return [{
          product_id: item.id,
          product_name: item.produt_name || item.name || 'Product',
          product_variant: 'Nil',
          qty: item.quantity,
          variant_id: null,
          unit_price: effectiveUnitPrice,
          amount: lineTotal,
          pricing_type: pricingType
        }];
      }).flat();

      // Prepare order data for Node.js API
      const orderData = {
        domain: window.location.hostname,
        items: orderItems,
        customer_name: customerDetails?.fullName || '',
        customer_email: customerDetails?.email || '',
        customer_phone: customerDetails?.phone || '',
        shipping_address: {
          address: customerDetails?.address || '',
          city: selectedLocation?.location || '',
          state: selectedState || '',
          country: selectedCountry || ''
        },
        billing_address: {
          address: customerDetails?.address || '',
          city: selectedLocation?.location || '',
          state: selectedState || '',
          country: selectedCountry || ''
        },
        notes: customerDetails?.note || '',
        shipping_method: 'standard',
        payment_method: 'monnify',
        payment_reference: paymentReference,
        payment_status: 'completed',
        amount: total,
        currency: 'NGN'
      };

      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();

      // Backend returns 'status' field and data structure: { status: true, orderID: "...", trackingId: "...", data: { orderId: ... } }
      if (data.status && (data.orderID || data.data?.orderId)) {
        clearCart();
        navigate('/order-complete', {
          state: {
            orderId: data.data?.orderId || data.orderID,
            trackingId: data.trackingId || data.data?.orderNumber
          }
        });
        return data;
      } else {
        throw new Error(data.error || data.message || 'Failed to place order');
      }
    } catch (error) {
      console.error('Error submitting Monnify order:', error);
      let errorMessage = 'Failed to place order. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast.error(errorMessage);
      toast.error('Payment was successful but order creation failed. Please contact support with reference: ' + paymentReference);
      
      return null;
    }
  };

  const uploadPaymentReceipts = async (paymentId: number) => {
    const uploadToast = toast.loading('Uploading payment receipts...');
    
    try {
      const formData = new FormData();
      
      if (orderPaymentReceipt) {
        formData.append('receipt', orderPaymentReceipt);
      }
      
      if (shippingPaymentReceipt) {
        formData.append('receipt', shippingPaymentReceipt);
      }
      
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/payments/${paymentId}/receipt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
          'X-Store-ID': storeInfo?.id?.toString() || ''
        },
        body: formData,
      });
      
      if (!response.ok) {
        toast.error('Receipt upload failed. Our team will contact you.', {
          id: uploadToast
        });
        return;
      }
      
      const result = await response.json();
      if (result.success) {
        toast.success('Payment receipts uploaded successfully', {
          id: uploadToast
        });
      }
    } catch (error) {
      console.error('Error uploading payment receipts:', error);
      
      // Network error handling
      if (!navigator.onLine) {
        toast.error('Internet connection lost. Please reconnect and try again', {
          id: uploadToast,
          duration: 5000
        });
      } else if (error instanceof TypeError && error.message.includes('fetch')) {
        toast.error('Server is unreachable. We\'ll automatically retry when possible', {
          id: uploadToast,
          duration: 5000
        });
      } else {
        toast.error('Receipt upload failed. Our team will handle this manually.', {
          id: uploadToast,
          duration: 5000
        });
      }

      // Store failed upload for recovery
      const failedUploads = JSON.parse(localStorage.getItem('failedUploads') || '[]');
      const failedUpload = {
        paymentId,
        timestamp: new Date().toISOString(),
        hasOrderReceipt: !!orderPaymentReceipt,
        hasShippingReceipt: !!shippingPaymentReceipt,
        error: error instanceof Error ? error.message : 'Unknown error',
        retryCount: 0
      };
      
      failedUploads.push(failedUpload);
      localStorage.setItem('failedUploads', JSON.stringify(failedUploads));

      // Schedule retry if appropriate
      if (navigator.onLine && failedUpload.retryCount < 3) {
        setTimeout(() => {
          uploadPaymentReceipts(paymentId);
        }, 5000); // Retry after 5 seconds
      }
    } finally {
      // Don't reset loading state here - let the main submitOrder function handle it
    }
  };

  // Helper functions for transaction tracking
  const storeTransaction = (data: {
    reference: string;
    gateway: 'paystack' | 'monnify' | 'bank';
    amount: number;
    status: 'pending' | 'success' | 'failed';
    orderId?: string;
    error?: string;
  }) => {
    const transactions = JSON.parse(localStorage.getItem('transactions') || '[]');
    transactions.push({
      ...data,
      timestamp: new Date().toISOString(),
      storeId: storeInfo?.id,
      customerEmail: customerDetails?.email
    });
    localStorage.setItem('transactions', JSON.stringify(transactions));
  };

  const updateTransactionStatus = (reference: string, status: 'pending' | 'success' | 'failed', orderId?: string, error?: string) => {
    const transactions = JSON.parse(localStorage.getItem('transactions') || '[]');
    const updatedTransactions = transactions.map((t: any) => {
      if (t.reference === reference) {
        return {
          ...t,
          status,
          orderId,
          error,
          lastUpdated: new Date().toISOString()
        };
      }
      return t;
    });
    localStorage.setItem('transactions', JSON.stringify(updatedTransactions));
  };

  const submitOrder = async (formData: FormData, paymentReference?: string) => {
    const toastId = 'submit-order';
    
    try {
      // Validate store ID first
      if (!storeInfo?.id) {
        const message = 'Store information is missing. Please refresh the page and try again.';
        toast.error(message, { id: toastId });
        setIsSubmitting(false);
        setShowPaymentDialog(false); // Close modal on error
        return null;
      }

      // Create order items directly from cart state with correct pricing
      const orderItems = cart.map(item => {
        if (item.selectedVariations && item.selectedVariations.length > 0) {
          return item.selectedVariations.map(variation => {
            const effectiveUnitPrice = getEffectiveVariationPrice(item, variation, item.selectedVariations || []);
            const lineTotal = effectiveUnitPrice * variation.quantity;
            return {
              product_id: item.id,
              product_name: item.produt_name,
              product_variant: variation.variation,
              qty: variation.quantity,
              variant_id: variation.v_id,
              unit_price: effectiveUnitPrice,
              amount: lineTotal
            };
          });
        }
        const effectiveUnitPrice = getEffectiveProductPrice(item, item.quantity);
        const lineTotal = effectiveUnitPrice * item.quantity;
        return [{
          product_id: item.id,
          product_name: item.produt_name,
          product_variant: 'Nil',
          qty: item.quantity,
          variant_id: null,
          unit_price: effectiveUnitPrice,
          amount: lineTotal
        }];
      }).flat();

      // Validate items
      if (!orderItems || orderItems.length === 0) {
        const message = 'Cart is empty. Please add items to your cart.';
        toast.error(message, { id: toastId });
        setIsSubmitting(false);
        setShowPaymentDialog(false);
        return null;
      }

      console.log('Order items being sent:', orderItems);

      const orderData = {
        domain: formData.get('domain') || window.location.hostname,
        store_id: storeInfo?.id,
        items: orderItems,
        customer_name: formData.get('fullName') || '',
        customer_email: formData.get('email') || '',
        customer_phone: formData.get('phone') || '',
        shipping_address: {
          address: formData.get('address') || '',
          city: formData.get('location') || '',
          state: selectedState || '',
          country: formData.get('country') || ''
        },
        billing_address: {
          address: formData.get('address') || '',
          city: formData.get('location') || '',
          state: selectedState || '',
          country: formData.get('country') || ''
        },
        notes: formData.get('note') || '',
        shipping_method: 'standard',
        payment_method: formData.get('paymentMethod') || 'bank',
        payment_reference: formData.get('paymentReference') || paymentReference || '',
        shipping_cost: parseFloat(formData.get('shippingAmount') as string || '0'),
        vat_amount: parseFloat(formData.get('vatAmount') as string || '0'),
        subtotal: parseFloat(formData.get('orderAmount') as string || '0'),
        total_amount: parseFloat(formData.get('orderAmount') as string || '0') + parseFloat(formData.get('shippingAmount') as string || '0') + parseFloat(formData.get('vatAmount') as string || '0')
      };

      let response;
      try {
        const requestPayload = {
          ...orderData,
          paymentReceipt: formData.get('paymentReceipt') || '',
          shippingPaymentReceipt: formData.get('shippingPaymentReceipt') || '',
          metadata: {
            store_id: storeInfo?.id,
            platform: 'web',
            version: '2.0'
          }
        };

        console.log('Sending order request:', {
          endpoint: `${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`,
          itemsCount: requestPayload.items?.length,
          storeId: requestPayload.store_id,
          paymentMethod: requestPayload.payment_method,
          hasPaymentReceipt: !!requestPayload.paymentReceipt && requestPayload.paymentReceipt !== '',
          hasShippingReceipt: !!requestPayload.shippingPaymentReceipt && requestPayload.shippingPaymentReceipt !== ''
        });

        response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Store-ID': storeInfo?.id?.toString() || ''
          },
          body: JSON.stringify(requestPayload)
        });
      } catch (error) {
        handleNetworkError(error, toastId);
        setIsSubmitting(false);
        setShowPaymentDialog(false); // Close modal on error
        throw error;
      }

      console.log('Response status:', response.status);
      
      const data = await response.json();
      console.log('Response data:', data);
      
      // Check for API errors (only call once)
      const errorMessage = handleApiError(response, data, toastId);
      if (errorMessage) {
        console.error('API Error:', errorMessage, 'Status:', response.status);
        // Handle specific error cases
        if (response.status === 400 && data.message?.toLowerCase().includes('store id')) {
          localStorage.removeItem('storeId');
        } else if (response.status === 404) {
          toast.error(`Endpoint not found: ${import.meta.env.VITE_API_ENDPOINT}/api/v1/orders`, { id: toastId });
        }
        setIsSubmitting(false);
        setShowPaymentDialog(false); // Close modal on error
        throw new Error(errorMessage);
      }

      // Check for success status
      if (!data || (!data.success && !data.status)) {
        const message = data?.message || 'Failed to process order';
        toast.error(message, { id: toastId });
        setIsSubmitting(false);
        setShowPaymentDialog(false); // Close modal on error
        throw new Error(message);
      }
      
      // Check for order ID in response
      if (!data.data?.orderId) {
        const message = 'Order ID is missing from response';
        toast.error(message, { id: toastId });
        setIsSubmitting(false);
        setShowPaymentDialog(false); // Close modal on error
        throw new Error(message);
      }

      // Order created successfully! Show success message
      toast.success('Order placed successfully!', { id: toastId });
      
      // Handle receipt upload for bank transfers if needed
      if (data.data.requiresReceipt && data.data.paymentId && (orderPaymentReceipt || shippingPaymentReceipt)) {
        try {
          await uploadPaymentReceipts(data.data.paymentId);
        } catch (uploadError) {
          console.error('Receipt upload failed:', uploadError);
          toast.error('Receipt upload failed but order was created. Please contact support.', { id: `${toastId}-receipt` });
        }
      }
      
      // Update transaction status for bank transfers
      if (formData.get('bankTransferRef')) {
        updateTransactionStatus(
          formData.get('bankTransferRef') as string,
          'success',
          data.data.orderId
        );
      }
      
      // Clear cart and navigate to order complete page
      clearCart();
      setIsSubmitting(false);
      setShowPaymentDialog(false); // Close modal on success
      
      navigate('/order-complete', {
        state: {
          orderId: data.data.orderId,
          trackingId: data.data.orderNumber,
          paymentReference: formData.get('bankTransferRef')
        }
      });
      
      return data;
    } catch (error) {
      console.error('Error submitting order:', error);
      handleNetworkError(error, toastId);
      
      // Ensure button state is reset on error
      setIsSubmitting(false);
      setShowPaymentDialog(false); // Close modal on error
      
      // Log and handle payment-related errors specially
      if (paymentReference) {
        const supportMessage = 'Payment was successful but order creation failed. Please contact support with reference: ' + paymentReference;
        toast.error(supportMessage, { id: `${toastId}-payment` });
        
        // Store the payment reference for recovery
        const failedOrders = JSON.parse(localStorage.getItem('failedOrders') || '[]');
        failedOrders.push({
          reference: paymentReference,
          amount: total,
          timestamp: new Date().toISOString(),
          storeId: storeInfo?.id,
          error: error instanceof Error ? error.message : String(error)
        });
        localStorage.setItem('failedOrders', JSON.stringify(failedOrders));

        // Log errors for tracking
        const errorLog = JSON.parse(localStorage.getItem('orderErrors') || '[]');
        errorLog.push({
          timestamp: new Date().toISOString(),
          paymentReference,
          error: error instanceof Error ? error.message : String(error),
          errorType: error instanceof Error ? error.constructor.name : typeof error
        });
        localStorage.setItem('orderErrors', JSON.stringify(errorLog));
      }

      setIsSubmitting(false);
      
      // Set retry state for non-critical errors
      if (error instanceof Error && 
          !error.message.includes('Store ID is missing') && 
          !error.message.includes('Store not found')) {
        // You can add state to show a retry button if needed
      }
      
      return null;
    }
  };

  const pageTitle = `Shopping Cart${storeInfo?.site_name ? ` - ${storeInfo.site_name}` : ''}`;

  const StockUpdateModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center mb-4">
          <AlertCircle className="h-6 w-6 text-yellow-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            Stock Level Updates
          </h3>
        </div>
        <div className="space-y-4">
          <p className="text-gray-600">
            Some items in your cart have reduced stock levels:
          </p>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {stockUpdates.map((update, index) => (
              <div key={index} className="text-sm">
                <p className="font-medium text-gray-900">{update.name}</p>
                <p className="text-gray-600">
                  {update.variation ? `${update.variation}: ` : ''}
                  Quantity will be reduced from {update.oldQty} to {update.newQty}
                </p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-6 flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={() => setShowStockUpdateModal(false)}
          >
            Review Later
          </Button>
          <Button
            onClick={handleStockUpdateConfirm}
          >
            Update Quantities
          </Button>
        </div>
      </div>
    </div>
  );

  const SoldOutModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <AlertCircle className="h-6 w-6 text-red-500 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Items No Longer Available
            </h3>
          </div>
          <button
            onClick={() => setShowSoldOutModal(false)}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="text-gray-600 mb-4">
          One or more items in your cart are no longer available. Please review your cart and remove the sold out items to continue.
        </p>
        <Button
          onClick={() => setShowSoldOutModal(false)}
          className="w-full"
        >
          Review Cart
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content="Review your cart and complete your purchase" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-brand-50/40 to-gray-50 py-6 sm:py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 mb-6 sm:mb-8 flex items-center gap-2">
            <span className="text-brand-600">Shopping Cart</span>
          </h1>

          {cart.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg border border-brand-100 p-8 text-center">
              <p className="text-gray-600">Your cart is empty</p>
              <Button
                onClick={() => window.history.back()}
                className="mt-4 bg-brand-500 hover:bg-brand-600"
              >
                Continue Shopping
              </Button>
            </div>
          ) : (
            <div className="lg:grid lg:grid-cols-12 lg:gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-7">
                <div className="bg-white rounded-2xl shadow-md border border-brand-100 overflow-hidden">
                  <ul className="divide-y divide-gray-200">
                    {cart.map((item) => (
                      <CartItemComponent
                        key={item.id}
                        item={item}
                        onQuantityChange={handleQuantityChange}
                        onVariationQuantityChange={handleVariationQuantityChange}
                        onRemoveVariation={handleRemoveVariation}
                        onRemoveFromCart={removeFromCart}
                        soldOutItems={soldOutItems}
                        stockLevels={stockLevels}
                      />
                    ))}
                  </ul>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-5 mt-8 lg:mt-0">
                <div className="bg-white rounded-2xl shadow-md border border-brand-100 overflow-hidden">
                  <div className="bg-gradient-to-r from-brand-50 to-brand-100/80 px-5 py-4 border-b border-brand-200">
                    <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-lg bg-brand-500/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                      </span>
                      Order Summary
                    </h2>
                  </div>
                  <div className="p-5 sm:p-6 space-y-5">
                  <CountryStateSelector
                    locations={allLocations}
                    selectedCountry={selectedCountry}
                    selectedState={selectedState}
                    countrySearch={countrySearch}
                    stateSearch={stateSearch}
                    showCountryDropdown={showCountryDropdown}
                    showStateDropdown={showStateDropdown}
                    onCountrySearchChange={setCountrySearch}
                    onStateSearchChange={setStateSearch}
                    onCountrySelect={handleCountryChange}
                    onStateSelect={handleStateChange}
                    onToggleCountryDropdown={() => setShowCountryDropdown(!showCountryDropdown)}
                    onToggleStateDropdown={() => setShowStateDropdown(!showStateDropdown)}
                  />

                  {/* Location Selection */}
                  <LocationSelector
                    locations={allLocations}
                    selectedLocation={selectedLocation}
                    locationSearch={locationSearch}
                    onLocationSearchChange={setLocationSearch}
                    onLocationSelect={handleLocationSelect}
                    showLocationDropdown={showLocationDropdown}
                    onToggleLocationDropdown={() => setShowLocationDropdown(!showLocationDropdown)}
                    selectedCountry={selectedCountry}
                    selectedState={selectedState}
                    stockPileEnabled={stockPileEnabled}

                  />

                  {/* Shipping Options */}
                  {isShipbubbleEnabled(storeInfo) && customerDetails?.address && selectedCountry && selectedState && (
                    <ShippingOptions
                      storeInfo={storeInfo}
                      cartItems={cart}
                      destination={{
                        address: customerDetails.address,
                        city: selectedLocation?.location || '',
                        state: selectedState,
                        country: selectedCountry
                      }}
                      manualShippingCost={shippingCost}
                      selectedOption={selectedShippingOption}
                      onOptionSelect={setSelectedShippingOption}
                    />
                  )}

                  {/* Customer Details */}
                  <CustomerDetailsForm
                    customerDetails={customerDetails}
                    onCustomerDetailsChange={handleCustomerDetailsChange}
                    selectedLocation={!!selectedLocation}
                  />

                  {/* Discount Summary */}
                  <DiscountSummary cart={cart} />

                  {/* Totals */}
                  <div className="border-t border-brand-200 pt-4 mt-4 bg-brand-50/30 rounded-xl p-4 -mx-1">
                    {totalDiscount > 0 && (
                      <>
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Original Subtotal</span>
                          <span className="line-through">{formatPrice(originalSubtotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1 text-brand-600">
                          <span>Bulk Discounts</span>
                          <span className="font-medium">-{formatPrice(totalDiscount)}</span>
                        </div>
                      </>
                    )}
                    <div className="flex justify-between text-sm text-gray-700 mt-2">
                      <span>Subtotal</span>
                      <span className="font-medium">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-700 mt-1">
                      <span>Shipping</span>
                      <span className="font-medium">{formatPrice(shippingCost)}</span>
                    </div>
                    {vatValue > 0 && (
                      <div className="flex justify-between text-sm text-gray-700 mt-1">
                        <span>VAT {vatType === 'percentage' ? `(${vatValue}%)` : ''}</span>
                        <span className="font-medium">{formatPrice(vatAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-lg font-bold mt-3 pt-3 border-t border-brand-200">
                      <span className="text-gray-900">Total</span>
                      <span className="text-brand-600">{formatPrice(total)}</span>
                    </div>
                    {totalDiscount > 0 && (
                      <div className="mt-3 p-2.5 bg-brand-100/80 rounded-lg border border-brand-200 text-center">
                        <span className="text-sm font-medium text-brand-700">
                          🎉 You saved {formatPrice(totalDiscount)} with bulk discounts!
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 pt-1">
                    <Button
                      onClick={() => {
                        const errors = getValidationErrors();
                        if (errors.length > 0) {
                          toast.error(errors[0]); // Show the first error
                        } else {
                          setShowPaymentDialog(true);
                        }
                      }}
                      className="w-full h-14 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed relative"
                      disabled={isSubmitting || soldOutItems.length > 0 || !isFormValid()}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center space-x-2">
                          <Loader2 className="w-6 h-6 animate-spin" />
                          <span className="animate-pulse">
                            {paymentMethod === 'bank' ? 'Processing Payment...' :
                             paymentMethod === 'paystack' ? 'Initializing Paystack...' :
                             paymentMethod === 'monnify' ? 'Initializing Monnify...' :
                             'Processing...'}
                          </span>
                        </div>
                      ) : soldOutItems.length > 0 ? (
                        <div className="flex items-center justify-center space-x-2">
                          <AlertCircle className="w-5 h-5" />
                          <span>Remove Sold Out Items to Continue</span>
                        </div>
                      ) : !isFormValid() ? (
                        <div className="flex items-center justify-center space-x-2">
                          <AlertCircle className="w-5 h-5" />
                          <span>{getValidationErrors()[0]}</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <span>Proceed to Payment</span>
                        </div>
                      )}
                    </Button>
                    {!isFormValid() && (
                      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="font-medium mb-1">Please fix the following:</p>
                        <ul className="list-disc list-inside space-y-0.5">
                          {getValidationErrors().slice(0, 3).map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                          {getValidationErrors().length > 3 && (
                            <li>...and {getValidationErrors().length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Note Dialog */}
      {storeInfo?.checkout_note && (
        <CheckoutNoteDialog
          open={showCheckoutNote}
          onClose={() => setShowCheckoutNote(false)}
          note={storeInfo.checkout_note}
        />
      )}

      {/* Payment Method Dialog */}
      <PaymentMethodDialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={setPaymentMethod}
        onConfirm={() => {
          if (location.state?.paymentStatus === 'success') {
            handleSubmitOrder();
          } else if (paymentMethod === 'paystack') {
            handlePaystackPayment();
          } else if (paymentMethod === 'monnify') {
            handleMonnifyPayment();
          } else if (paymentMethod === 'stripe') {
            // Stripe handled internally in the dialog
          } else {
            handleSubmitOrder();
          }
          // Don't close modal immediately - let the payment functions handle it after API response
        }}
        orderTotal={subtotal}
        shippingTotal={shippingCost}
        vatAmount={vatAmount}
        customerEmail={customerDetails?.email}
        customerName={customerDetails?.fullName}
        storeInfo={storeInfo}
        onFileUpload={handleFileUpload}
        orderPaymentReceipt={orderPaymentReceipt}
        shippingPaymentReceipt={shippingPaymentReceipt}
        cart={cart}
        isLoading={isSubmitting}
      />

      {showStockUpdateModal && <StockUpdateModal />}
      {showSoldOutModal && <SoldOutModal />}
    </>
  );
}






